import type { ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { lessonStepClassNames, lessonStepStyles } from './LessonStep.styles';

export interface LessonStepProps {
  stepNumber: number;
  title: string;
  status?: ReactNode;
  children: ReactNode;
}

interface TitleParagraph {
  label: string;
  segments: TitleSegment[];
}

interface TitleSegment {
  isBold: boolean;
  text: string;
}

export function LessonStep({
  stepNumber,
  title,
  status,
  children,
}: LessonStepProps) {
  const theme = useTheme();

  return (
    <Paper
      component="article"
      className={lessonStepClassNames.root}
      elevation={0}
      sx={lessonStepStyles.root(theme)}
    >
      <Box
        className={lessonStepClassNames.marker}
        sx={lessonStepStyles.marker(theme)}
        aria-hidden="true"
      >
        {stepNumber}
      </Box>
      <Box className={lessonStepClassNames.content} sx={lessonStepStyles.content(theme)}>
        <Typography
          component="h3"
          sx={lessonStepStyles.title(theme)}
          aria-label={getTitleLabel(title)}
        >
          {renderStepTitle(title)}
        </Typography>
        {children}
      </Box>
      <Box className={lessonStepClassNames.status} sx={lessonStepStyles.status(theme)}>
        {status}
      </Box>
    </Paper>
  );
}

function renderStepTitle(title: string): ReactNode {
  const paragraphs = parseTitle(title);

  if (paragraphs.length <= 1) {
    return renderTitleSegments(paragraphs[0]?.segments || []);
  }

  return paragraphs.map((paragraph, index) => (
    <Box
      component="span"
      className={lessonStepClassNames.titleParagraph}
      sx={lessonStepStyles.titleParagraph()}
      key={index}
    >
      {renderTitleSegments(paragraph.segments)}
    </Box>
  ));
}

function getTitleLabel(title: string): string {
  return parseTitle(title)
    .map((paragraph) => paragraph.label)
    .join(' ');
}

function parseTitle(title: string): TitleParagraph[] {
  return title
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => {
      const segments = parseTitleSegments(paragraph);

      return {
        label: segments.map((segment) => segment.text).join(''),
        segments,
      };
    });
}

function parseTitleSegments(text: string): TitleSegment[] {
  const segments: TitleSegment[] = [];
  const boldPattern = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match = boldPattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      segments.push({
        isBold: false,
        text: text.slice(lastIndex, match.index),
      });
    }

    segments.push({
      isBold: true,
      text: match[1],
    });

    lastIndex = match.index + match[0].length;
    match = boldPattern.exec(text);
  }

  if (lastIndex < text.length) {
    segments.push({
      isBold: false,
      text: text.slice(lastIndex),
    });
  }

  return segments;
}

function renderTitleSegments(segments: TitleSegment[]): ReactNode {
  return segments.map((segment, index) => {
    if (!segment.isBold) {
      return segment.text;
    }

    return (
      <Box
        component="strong"
        className={lessonStepClassNames.titleStrong}
        sx={lessonStepStyles.titleStrong()}
        key={`strong-${index}`}
      >
        {segment.text}
      </Box>
    );
  });
}
